import {
  DashboardSummaryDetailResponse,
  SummaryDetailOrder,
  SummaryTypeSlice,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const fmtD = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
};

const paymentStatusKo: Record<string, string> = {
  Paid: '결제완료',
  Confirmed: '확인됨',
  CancelRequested: '취소요청',
  Canceled: '취소됨',
  Failed: '실패',
  Pending: '대기중',
};

const cancelTypeKo: Record<string, string> = {
  None: '-',
  Full: '전체취소',
  Partial: '부분취소',
};

/**
 * 취소 건의 환불 경로. 취소가 없으면 '-'.
 * 이체환불 = 가상계좌·계좌입금이라 PG 자동환불이 안 돼 우리가 통장에서 직접 이체한 것.
 */
const refundRouteLabel = (o: { cancelType: string; refundRoute: string }): string =>
  o.cancelType === 'None' ? '-' : o.refundRoute === 'Manual' ? '이체환불' : '자동환불';

const paymentMethodKo: Record<string, string> = {
  card: '카드',
  vbank: '가상계좌',
  trans: '계좌이체',
  phone: '휴대폰',
  cash: '현금',
};

const NUM_FMT = '#,##0';
/** 수수료율처럼 0~1 소수로 오는 값. 셀엔 소수를 넣고 표시만 % 로 돌린다. */
const PCT_FMT = '0.00%';

function charWidth(s: string): number {
  let w = 0;
  for (const ch of s) {
    w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
  }
  return w;
}

function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 6;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v != null) {
        const w = charWidth(String(cell.v));
        if (w > max) max = w;
      }
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 3, 45) });
  }
  ws['!cols'] = cols;
}

function applyNumberFormat(ws: XLSX.WorkSheet, numCols: number[], z: string = NUM_FMT) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (let i = 0; i < numCols.length; i++) {
      const C = numCols[i];
      if (C === undefined) continue;
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (cell && typeof cell.v === 'number') {
        cell.z = z;
      }
    }
  }
}

function freezeHeader(ws: XLSX.WorkSheet) {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = {
    state: 'frozen',
    ySplit: 1,
  };
}

function findNumCols(ws: XLSX.WorkSheet, labels: string[]): number[] {
  const result: number[] = [];
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && labels.includes(String(hdr.v))) result.push(C);
  }
  return result;
}

function finishSheet(ws: XLSX.WorkSheet, moneyLabels: string[], pctLabels?: string[]) {
  autoWidth(ws);
  freezeHeader(ws);
  const numCols = findNumCols(ws, moneyLabels);
  if (numCols.length > 0) applyNumberFormat(ws, numCols);
  if (pctLabels?.length) {
    const pctCols = findNumCols(ws, pctLabels);
    if (pctCols.length > 0) applyNumberFormat(ws, pctCols, PCT_FMT);
  }
}

/** 헤더 행에 필터를 건다. 건수가 많은 시트에서 상태·상조별로 추려 보기 위함. */
function addAutoFilter(ws: XLSX.WorkSheet) {
  if (ws['!ref']) ws['!autofilter'] = { ref: ws['!ref'] };
}

function saveWorkbook(wb: XLSX.WorkBook, filename: string) {
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename);
}

/* ── 매출 구성 건별 엑셀 (요약탭) ── */

const SUMMARY_CHECK_HEAD = ['항목', '요약탭', '건별 합계', '차액'];

const SUMMARY_ORDER_MONEY = [
  '총매출',
  '취소액',
  '실이체액',
  '순매출',
  '면세 매출',
  '과세 매출',
  '수수료 base',
  '수수료',
  '포인트 결제',
];

const SUMMARY_TYPE_MONEY = [
  '쿠폰 매출',
  '쿠폰 취소',
  '쿠폰 순매출',
  '배송 매출',
  '배송 취소',
  '배송 순매출',
  '기타 매출',
  '기타 취소',
  '기타 순매출',
];

const SUMMARY_ITEM_MONEY = [
  '카탈로그 단가',
  '옵션 추가금',
  '소량주문 추가금',
  '청구단가',
  '라인 매출',
  '라인 취소',
  '라인 순매출',
];

const EMPTY_SLICE: SummaryTypeSlice = {
  grossPayment: 0,
  cancelAmount: 0,
  cancelAmountManual: 0,
  cancelAmountAuto: 0,
  netSales: 0,
  quantity: 0,
  cancelQuantity: 0,
  netQuantity: 0,
};

/** 주문별 byType 슬라이스를 축(coupon/delivery/etc)별로 합산. 검산 시트가 쓴다. */
const sumSlice = (
  orders: SummaryDetailOrder[],
  key: keyof SummaryDetailOrder['byType'],
) =>
  orders.reduce<SummaryTypeSlice>(
    (t, o) => {
      const s = o.byType?.[key] ?? EMPTY_SLICE;
      return {
        grossPayment: t.grossPayment + s.grossPayment,
        cancelAmount: t.cancelAmount + s.cancelAmount,
        cancelAmountManual: t.cancelAmountManual + s.cancelAmountManual,
        cancelAmountAuto: t.cancelAmountAuto + s.cancelAmountAuto,
        netSales: t.netSales + s.netSales,
        quantity: t.quantity + s.quantity,
        cancelQuantity: t.cancelQuantity + s.cancelQuantity,
        netQuantity: t.netQuantity + s.netQuantity,
      };
    },
    { ...EMPTY_SLICE },
  );

const uniq = <T>(values: T[]) => Array.from(new Set(values));

/** 모든 행에서 비어 있거나 0인 열. 표에 남겨둘 이유가 없다. */
const allBlank = (rows: Record<string, unknown>[], key: string) =>
  rows.every((r) => !r[key]);

const dropColumns = (rows: Record<string, unknown>[], keys: string[]) => {
  for (const r of rows) for (const k of keys) delete r[k];
};

/** 표에서 뺀 대신 표지 머리말로 올리는 고정값. */
interface SummaryPin {
  label: string;
  value: string | number;
  /** 0~1 소수를 % 서식으로 보여줄지 */
  pct?: boolean;
}

/**
 * 검산 표 — 요약탭 화면에 뜬 합계(`totals`)와 아래 시트들의 건별 합을 나란히 놓는다.
 *
 * 수수료만 차액이 날 수 있다: 요약탭은 상조별로 모아 마지막에 한 번 반올림하고,
 * 건별 행은 주문마다 반올림하기 때문. 최대 `주문 수 × 0.5원`이라 그 이상 벌어지면 버그다.
 * 나머지 항목의 차액은 전부 0이어야 한다.
 */
function buildSummaryCheckRows(data: DashboardSummaryDetailResponse) {
  const t = data.totals;
  const o = data.orders;
  const sum = (pick: (r: SummaryDetailOrder) => number) =>
    o.reduce((s, r) => s + pick(r), 0);

  const coupon = sumSlice(o, 'coupon');
  const delivery = sumSlice(o, 'delivery');
  const etc = sumSlice(o, 'etc');

  const rows: Array<{ 항목: string; 요약탭: number; '건별 합계': number; 차액: number }> =
    [
      { 항목: '주문 수', 요약탭: t.orderCount, '건별 합계': o.length, 차액: 0 },
      {
        항목: '총매출',
        요약탭: t.grossPayment,
        '건별 합계': sum((r) => r.grossPayment),
        차액: 0,
      },
      {
        항목: '취소액',
        요약탭: t.cancelAmount,
        '건별 합계': sum((r) => r.cancelAmount),
        차액: 0,
      },
      {
        항목: '순매출',
        요약탭: t.grossSales,
        '건별 합계': sum((r) => r.netSales),
        차액: 0,
      },
      {
        항목: '면세 매출',
        요약탭: t.netTaxFreeSales,
        '건별 합계': sum((r) => r.netTaxFree),
        차액: 0,
      },
      {
        항목: '과세 매출',
        요약탭: t.netTaxableSales,
        '건별 합계': sum((r) => r.netTaxable),
        차액: 0,
      },
      {
        항목: '수수료 base',
        요약탭: t.netCashForServiceFee,
        '건별 합계': sum((r) => r.netCashForServiceFee),
        차액: 0,
      },
      {
        항목: '수수료',
        요약탭: t.serviceFee,
        '건별 합계': sum((r) => r.serviceFee),
        차액: 0,
      },
      {
        항목: '쿠폰형 순매출',
        요약탭: t.byType.coupon.netSales,
        '건별 합계': coupon.netSales,
        차액: 0,
      },
      {
        항목: '배송형 순매출',
        요약탭: t.byType.delivery.netSales,
        '건별 합계': delivery.netSales,
        차액: 0,
      },
      {
        항목: '기타(잔차) 순매출',
        요약탭: t.byType.etc.netSales,
        '건별 합계': etc.netSales,
        차액: 0,
      },
    ];

  for (const r of rows) r.차액 = r['건별 합계'] - r.요약탭;
  return rows;
}

function buildSummaryOrderRow(o: SummaryDetailOrder, idx: number, hideReceiver: boolean) {
  const r: Record<string, unknown> = { '#': idx + 1, 결제일: fmtD(o.purchasedAt) };
  r['상조'] = o.bugoBrandName ?? '';
  if (!hideReceiver) {
    r['구매자'] = o.receiverName ?? '';
    r['구매자 연락처'] = o.receiverPhoneNumber ?? '';
  }
  r['고인명'] = o.deceasedName ?? '';
  r['발인일'] = fmtD(o.coffinOutDate);
  r['행사등록자'] = o.feventUserName ?? '';
  r['행사등록자 연락처'] = o.feventUserPhone ?? '';
  r['결제상태'] = paymentStatusKo[o.paymentStatus] ?? o.paymentStatus;
  r['결제수단'] = paymentMethodKo[o.paymentMethod] ?? o.paymentMethod;
  r['취소구분'] = cancelTypeKo[o.cancelType] ?? o.cancelType;
  // 환불경로·실이체액은 외부 장부 대사용이라 상조 관리자(hideReceiver) 파일엔 아예 안 넣는다.
  if (!hideReceiver) r['환불경로'] = refundRouteLabel(o);
  r['총매출'] = o.grossPayment;
  r['취소액'] = o.cancelAmount;
  // 실이체액 — 이체환불(Manual) 검산용. 취소액과 어긋나면 원장 없는 레거시 취소다.
  if (!hideReceiver) {
    r['실이체액'] = o.refundRoute === 'Manual' ? o.manualRefundTransferred : 0;
  }
  r['순매출'] = o.netSales;
  r['면세 매출'] = o.netTaxFree;
  r['과세 매출'] = o.netTaxable;
  r['수수료 base'] = o.netCashForServiceFee;
  r['수수료율'] = o.feeRate;
  r['정산방식'] = o.settlementMethod === 'Rebate' ? '리베이트' : '수수료율';
  r['수수료'] = o.serviceFee;
  r['포인트 결제'] = o.paymentByPoint;
  return r;
}

function buildSummaryTypeRow(o: SummaryDetailOrder, idx: number, hideReceiver: boolean) {
  const bt = o.byType;
  const r: Record<string, unknown> = { '#': idx + 1, 결제일: fmtD(o.purchasedAt) };
  if (hideReceiver) {
    r['고인명'] = o.deceasedName ?? '';
  } else {
    r['주문ID'] = o.orderId;
    r['구매자'] = o.receiverName ?? '';
  }
  r['취소구분'] = cancelTypeKo[o.cancelType] ?? o.cancelType;
  if (!hideReceiver) r['환불경로'] = refundRouteLabel(o);
  r['쿠폰 매출'] = bt.coupon.grossPayment;
  r['쿠폰 취소'] = bt.coupon.cancelAmount;
  r['쿠폰 순매출'] = bt.coupon.netSales;
  r['쿠폰 순수량'] = bt.coupon.netQuantity;
  r['배송 매출'] = bt.delivery.grossPayment;
  r['배송 취소'] = bt.delivery.cancelAmount;
  r['배송 순매출'] = bt.delivery.netSales;
  r['배송 순수량'] = bt.delivery.netQuantity;
  r['기타 매출'] = bt.etc.grossPayment;
  r['기타 취소'] = bt.etc.cancelAmount;
  r['기타 순매출'] = bt.etc.netSales;
  return r;
}

function buildSummaryItemRows(o: SummaryDetailOrder, idx: number, hideReceiver: boolean) {
  return o.items.map((item) => {
    const r: Record<string, unknown> = {};
    if (hideReceiver) {
      r['주문 순번'] = idx + 1;
    } else {
      r['주문ID'] = o.orderId;
    }
    r['결제일'] = fmtD(o.purchasedAt);
    r['고인명'] = o.deceasedName ?? '';
    r['취소구분'] = cancelTypeKo[o.cancelType] ?? o.cancelType;
    if (!hideReceiver) r['환불경로'] = refundRouteLabel(o);
    r['상품명'] = item.giftItemName;
    r['유형'] = item.type === 'Coupon' ? '쿠폰' : '배송';
    r['과세여부'] = item.isTaxable ? '과세' : '면세';
    r['카탈로그 단가'] = item.priceRetail;
    r['옵션 추가금'] = item.priceDelta;
    r['소량주문 추가금'] = item.unitSurcharge;
    r['청구단가'] = item.unitPrice;
    r['수량'] = item.quantity;
    r['취소수량'] = item.cancelQuantity;
    r['순수량'] = item.netQuantity;
    r['라인 매출'] = item.lineGross;
    r['라인 취소'] = item.lineCancel;
    r['라인 순매출'] = item.lineNet;
    return r;
  });
}

/**
 * 표지 시트 — 조회 조건·고정값 머리말 + 검산 표.
 *
 * 머리말엔 `pins`가 온다. 상조 관리자는 상조·정산방식·수수료율이 전 행에 똑같이 반복되므로
 * 표에서 빼고 여기로 올린다. 값이 여러 개면(본사 관리자가 여러 상조를 볼 때) 표에 열로 남는다.
 */
function buildSummaryCoverSheet(
  data: DashboardSummaryDetailResponse,
  periodLabel: string,
  pins: SummaryPin[],
  hasPrunedColumns: boolean,
  agencyMode: boolean,
): XLSX.WorkSheet {
  const head: Array<Array<string | number>> = [
    [agencyMode ? '매출 구성 요약' : '매출 구성 · 건별 명세', periodLabel],
    ['조회 기간', `${fmtD(data.period.from)} ~ ${fmtD(data.period.to)}`],
    ...pins.map((p) => [p.label, p.value]),
  ];
  const checkRows = buildSummaryCheckRows(data);
  /** 머리말 아래 빈 줄 하나를 띄우고 표가 시작한다. */
  const tableHeadRow = head.length + 1;

  // 상조 관리자는 자기 매출 명세만 본다 — `요약탭 vs 건별 합계 vs 차액` 검산 프레이밍은
  // 우리 내부 대사용이라 위화감을 준다. 합계값(요약탭 기준, 권위값) 한 열만 남긴다.
  const aoa: Array<Array<string | number>> = agencyMode
    ? [
        ...head,
        [],
        ['항목', '금액'],
        ...checkRows.map((r) => [r.항목, r.요약탭]),
      ]
    : [
        ...head,
        [],
        [...SUMMARY_CHECK_HEAD],
        ...checkRows.map((r) => [r.항목, r.요약탭, r['건별 합계'], r.차액]),
      ];
  if (hasPrunedColumns) {
    aoa.push(
      [],
      [
        '* 전 주문에서 값이 같거나(위 머리말), 전부 0이거나, 다른 열과 중복인 열은 표에서 생략했습니다.',
      ],
    );
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  for (let r = tableHeadRow + 1; r <= tableHeadRow + checkRows.length; r++) {
    for (let c = 1; c <= 3; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && typeof cell.v === 'number') cell.z = NUM_FMT;
    }
  }
  pins.forEach((p, i) => {
    if (!p.pct) return;
    // 머리말 2줄(제목·조회 기간) 다음부터 pin이 놓인다
    const cell = ws[XLSX.utils.encode_cell({ r: 2 + i, c: 1 })];
    if (cell && typeof cell.v === 'number') cell.z = PCT_FMT;
  });

  autoWidth(ws);
  return ws;
}

/**
 * 상조·정산방식·수수료율은 상조 관리자에겐 전 행이 같은 값이라 열로 두면 읽기만 방해한다.
 * 역할로 분기하지 않고 **실제 값이 하나뿐인지**로 판단해서 표지 머리말로 올린다 —
 * 본사 관리자가 한 상조만 조회했을 때도 똑같이 정리된다.
 *
 * `agencyMode`면 상조명은 머리말에도 안 올린다 — 자기 페이지에서 자기 이름을 다시 보는 건
 * 위화감만 준다. 열은 그대로 뺀다.
 */
function pinConstantOrderColumns(orders: SummaryDetailOrder[], agencyMode: boolean) {
  const pins: SummaryPin[] = [];
  const drop: string[] = [];
  if (orders.length === 0) return { pins, drop };

  const brands = uniq(orders.map((o) => o.bugoBrandName ?? ''));
  if (brands.length === 1) {
    drop.push('상조');
    if (brands[0] && !agencyMode) pins.push({ label: '상조', value: brands[0] });
  }

  const methods = uniq(orders.map((o) => o.settlementMethod));
  if (methods.length === 1) {
    drop.push('정산방식');
    pins.push({
      label: '정산방식',
      value: methods[0] === 'Rebate' ? '리베이트' : '수수료율',
    });
  }

  const rates = uniq(orders.map((o) => o.feeRate));
  if (rates.length === 1 && rates[0] !== undefined) {
    drop.push('수수료율');
    pins.push({ label: '수수료율', value: rates[0], pct: true });
  }
  return { pins, drop };
}

/** 전부 0이거나 다른 열과 값이 똑같은 열은 뺀다. 어떤 열이 그런지는 데이터가 정한다. */
function pruneBlankOrderColumns(rows: Record<string, unknown>[]) {
  const drop: string[] = [];
  // 리베이트 상조는 수수료가 항상 0이라 두 열이 통째로 0으로 찬다
  if (allBlank(rows, '수수료')) drop.push('수수료', '수수료 base');
  // 과다취소(순매출<0)가 없으면 수수료 base는 순매출과 같은 열이다
  else if (rows.every((r) => r['수수료 base'] === r['순매출'])) drop.push('수수료 base');

  // 면세/과세는 한 축이 비면 나머지가 곧 순매출이다. 합계는 표지 검산 표에 그대로 남는다.
  if (allBlank(rows, '과세 매출')) drop.push('과세 매출', '면세 매출');
  else if (allBlank(rows, '면세 매출')) drop.push('면세 매출', '과세 매출');

  if (allBlank(rows, '포인트 결제')) drop.push('포인트 결제');
  // 이체환불이 한 건도 없으면 환불경로는 전부 '-', 실이체액은 전부 0 → 죽은 열이라 뺀다.
  if (rows.every((r) => r['환불경로'] === '-' || !r['환불경로'])) {
    drop.push('환불경로', '실이체액');
  } else if (allBlank(rows, '실이체액')) {
    drop.push('실이체액');
  }
  return drop;
}

/** 쿠폰형·배송형·기타 중 매출이 전혀 없는 축은 열 묶음째 뺀다 (요약탭이 `기타` 열을 감추는 것과 같은 규칙). */
function pruneEmptyTypeColumns(rows: Record<string, unknown>[]) {
  const groups = [
    ['쿠폰 매출', '쿠폰 취소', '쿠폰 순매출', '쿠폰 순수량'],
    ['배송 매출', '배송 취소', '배송 순매출', '배송 순수량'],
    ['기타 매출', '기타 취소', '기타 순매출'],
  ];
  return groups.filter((g) => g.every((k) => allBlank(rows, k))).flat();
}

/** 옵션 추가금·소량주문 추가금이 하나도 없으면 청구단가가 곧 카탈로그 단가다. */
function pruneFlatPriceColumns(rows: Record<string, unknown>[]) {
  const drop: string[] = [];
  if (allBlank(rows, '옵션 추가금')) drop.push('옵션 추가금');
  if (allBlank(rows, '소량주문 추가금')) drop.push('소량주문 추가금');
  if (drop.length === 2) drop.push('카탈로그 단가');
  return drop;
}

/**
 * 요약탭 `매출 구성 요약`을 주문별로 펼친 엑셀.
 *
 * `sales-detail`(매출 상세 탭)과 다른 물건이다 — 이쪽은 전체취소 주문을 포함하고
 * 수수료율도 상조별이라, 합계가 요약탭 화면 숫자와 그대로 대사된다.
 */
export function exportSummaryDetailExcel(
  data: DashboardSummaryDetailResponse,
  periodLabel: string,
  hideReceiver = false,
) {
  const wb = XLSX.utils.book_new();
  const hasOrders = data.orders.length > 0;

  // hideReceiver = 상조 관리자 모드. 자기 페이지처럼 보이게 상조명·검산 프레이밍을 정리한다.
  const agencyMode = hideReceiver;
  const { pins, drop: pinnedCols } = pinConstantOrderColumns(data.orders, agencyMode);

  const orderRows = data.orders.map((o, i) => buildSummaryOrderRow(o, i, hideReceiver));
  const orderDrop = hasOrders
    ? [...pinnedCols, ...pruneBlankOrderColumns(orderRows)]
    : [];
  dropColumns(orderRows, orderDrop);

  const typeRows = data.orders.map((o, i) => buildSummaryTypeRow(o, i, hideReceiver));
  const typeDrop = hasOrders ? pruneEmptyTypeColumns(typeRows) : [];
  dropColumns(typeRows, typeDrop);

  const itemRows = data.orders.flatMap((o, i) =>
    buildSummaryItemRows(o, i, hideReceiver),
  );
  const itemDrop = itemRows.length > 0 ? pruneFlatPriceColumns(itemRows) : [];
  dropColumns(itemRows, itemDrop);

  const prunedAny = orderDrop.length > 0 || typeDrop.length > 0 || itemDrop.length > 0;
  XLSX.utils.book_append_sheet(
    wb,
    buildSummaryCoverSheet(data, periodLabel, pins, prunedAny, agencyMode),
    agencyMode ? '매출 요약' : '요약·검산',
  );

  const wsOrders = XLSX.utils.json_to_sheet(
    hasOrders ? orderRows : [{ 안내: '해당 기간 주문 내역이 없습니다.' }],
  );
  finishSheet(wsOrders, SUMMARY_ORDER_MONEY, ['수수료율']);
  addAutoFilter(wsOrders);
  XLSX.utils.book_append_sheet(wb, wsOrders, '주문별 매출구성');

  if (hasOrders) {
    const wsTypes = XLSX.utils.json_to_sheet(typeRows);
    finishSheet(wsTypes, SUMMARY_TYPE_MONEY);
    addAutoFilter(wsTypes);
    XLSX.utils.book_append_sheet(wb, wsTypes, '주문별 유형분해');

    if (itemRows.length > 0) {
      const wsItems = XLSX.utils.json_to_sheet(itemRows);
      finishSheet(wsItems, SUMMARY_ITEM_MONEY);
      addAutoFilter(wsItems);
      XLSX.utils.book_append_sheet(wb, wsItems, '주문별 상품라인');
    }
  }

  saveWorkbook(wb, `매출구성_건별_${periodLabel}.xlsx`);
}
